import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  SlidersHorizontal,
  MapPin,
  ArrowRight,
} from 'lucide-react'
import { appRoutes } from '@/constants'

// Forest road Porsche image - exact match to Figma reference
const HERO_SLIDES = [
  'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?q=90&w=2000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?q=90&w=2000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=90&w=2000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?q=90&w=2000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1580274455191-1c62238fa1c6?q=90&w=2000&auto=format&fit=crop',
]

export function HeroSection() {
  const [activeSlide, setActiveSlide] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [category, setCategory] = useState('')
  const [locationZip, setLocationZip] = useState('')

  const navigate = useNavigate()

  // Auto-slide every 4 seconds
  const nextSlide = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % HERO_SLIDES.length)
  }, [])

  useEffect(() => {
    const timer = setInterval(nextSlide, 4000)
    return () => clearInterval(timer)
  }, [nextSlide])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams()
    if (keyword) params.append('q', keyword)
    if (category) params.append('category', category)
    if (locationZip) params.append('location', locationZip)
    navigate(`${appRoutes.parts}?${params.toString()}`)
  }

  return (
    <section className="relative w-full pt-3 pb-8 bg-background" style={{ overflow: 'hidden' }}>

      {/* ── LEFT wavy lines — visible in margin beside hero card ── */}
      <svg
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '220px',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
        viewBox="0 0 220 800"
        preserveAspectRatio="xMinYMin meet"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {[0, 16, 32, 48, 64, 80, 96, 112, 128].map((offset, i) => (
          <path
            key={i}
            d={`M ${-40 + offset} 0 C ${60 + offset} 160, ${-10 + offset} 320, ${80 + offset} 480 S ${30 + offset} 660, ${100 + offset} 820`}
            stroke="#b0b8cc"
            strokeWidth="1.3"
            opacity={Math.max(0.06, 0.28 - i * 0.025)}
            strokeLinecap="round"
          />
        ))}
      </svg>

      {/* ── RIGHT wavy lines — visible in margin beside hero card ── */}
      <svg
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '220px',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
          transform: 'scaleX(-1)',
        }}
        viewBox="0 0 220 800"
        preserveAspectRatio="xMinYMin meet"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {[0, 16, 32, 48, 64, 80, 96, 112, 128].map((offset, i) => (
          <path
            key={i}
            d={`M ${-40 + offset} 0 C ${60 + offset} 160, ${-10 + offset} 320, ${80 + offset} 480 S ${30 + offset} 660, ${100 + offset} 820`}
            stroke="#b0b8cc"
            strokeWidth="1.3"
            opacity={Math.max(0.06, 0.28 - i * 0.025)}
            strokeLinecap="round"
          />
        ))}
      </svg>

      <div className="mx-auto max-w-[1440px] px-2 sm:px-3" style={{ position: 'relative', zIndex: 1 }}>

        {/* ─── HERO BANNER CARD ─────────────────────────────────────── */}
        <div
          className="relative overflow-hidden bg-slate-950 shadow-2xl w-full"
          style={{ borderRadius: '20px', minHeight: '520px' }}
        >
          {/* Background image — exact Figma: dark forest road with car */}
          <div
            className="absolute inset-0 bg-cover bg-center transition-all duration-700"
            style={{
              backgroundImage: `url('${HERO_SLIDES[activeSlide]}')`,
            }}
          />

          {/* Left-heavy dark gradient for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

          {/* ── OVERLAY CONTENT ── */}
          <div className="relative z-10 flex flex-col justify-end h-full min-h-[420px] px-8 sm:px-12 lg:px-16 pb-14 sm:pb-16">
            <div className="max-w-lg space-y-3">
              {/* Label */}
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: '11px',
                  letterSpacing: '0.2em',
                }}
                className="text-white/70 uppercase block"
              >
                FEATURED COLLECTION
              </span>

              {/* Heading */}
              <h1
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: 'clamp(2rem, 5vw, 3.25rem)',
                  lineHeight: '1.05',
                }}
                className="text-white"
              >
                Performance Parts
              </h1>

              {/* Subtitle */}
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '1.6',
                }}
                className="text-white/80 max-w-sm"
              >
                Boost horsepower, handling &amp; style with our performance collection.
              </p>

              {/* CTA Button */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => navigate(appRoutes.parts)}
                  style={{
                    backgroundColor: '#CF2F2F',
                    borderRadius: '8px',
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 800,
                    fontSize: '11px',
                    letterSpacing: '0.05em',
                  }}
                  className="inline-flex items-center gap-2 px-5 py-3 text-white uppercase shadow-lg transition-all hover:opacity-90 active:scale-95"
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                  <span>UPGRADE NOW</span>
                </button>
              </div>
            </div>
          </div>

          {/* ── CAROUSEL DOTS — centered, above bottom edge ── */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
            {HERO_SLIDES.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveSlide(idx)}
                style={{
                  backgroundColor: idx === activeSlide ? '#CF2F2F' : 'rgba(255,255,255,0.4)',
                  width: idx === activeSlide ? '28px' : '8px',
                  height: '8px',
                  borderRadius: '9999px',
                  transition: 'all 0.3s',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* ─── FLOATING SEARCH CARD — overlaps bottom of hero ─────── */}
        <div
          className="relative z-20 mx-auto"
          style={{ marginTop: '-52px', maxWidth: '860px', padding: '0 8px' }}
        >
          <div
            className="bg-white border border-gray-200 shadow-xl"
            style={{ borderRadius: '12px', padding: '18px 20px 16px' }}
          >
            <form onSubmit={handleSearch}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1px 1fr 1px 1fr auto',
                  alignItems: 'center',
                  gap: '0',
                }}
              >
                {/* Field 1: Search Cars / Parts */}
                <div style={{ padding: '0 16px 0 4px' }}>
                  <label
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: '11px',
                      color: '#111',
                      display: 'block',
                      marginBottom: '6px',
                    }}
                  >
                    Search Cars / Parts
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="e.g. Headlights, Porsche"
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      style={{
                        width: '100%',
                        height: '38px',
                        border: 'none',
                        outline: 'none',
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#333',
                        background: 'transparent',
                        paddingRight: '28px',
                      }}
                    />
                    <Search className="absolute right-0 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Divider */}
                <div style={{ width: '1px', height: '40px', backgroundColor: '#e5e7eb', flexShrink: 0 }} />

                {/* Field 2: Category */}
                <div style={{ padding: '0 16px' }}>
                  <label
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: '11px',
                      color: '#111',
                      display: 'block',
                      marginBottom: '6px',
                    }}
                  >
                    Category
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Performance Parts"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      style={{
                        width: '100%',
                        height: '38px',
                        border: 'none',
                        outline: 'none',
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#333',
                        background: 'transparent',
                        paddingRight: '28px',
                      }}
                    />
                    <SlidersHorizontal className="absolute right-0 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Divider */}
                <div style={{ width: '1px', height: '40px', backgroundColor: '#e5e7eb', flexShrink: 0 }} />

                {/* Field 3: Location / Zip */}
                <div style={{ padding: '0 16px' }}>
                  <label
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: '11px',
                      color: '#111',
                      display: 'block',
                      marginBottom: '6px',
                    }}
                  >
                    Location / Zip
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="e.g. 110001"
                      value={locationZip}
                      onChange={(e) => setLocationZip(e.target.value)}
                      style={{
                        width: '100%',
                        height: '38px',
                        border: 'none',
                        outline: 'none',
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '12px',
                        color: '#333',
                        background: 'transparent',
                        paddingRight: '28px',
                      }}
                    />
                    <MapPin className="absolute right-0 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* FIND NOW Button */}
                <div style={{ paddingLeft: '16px' }}>
                  <button
                    type="submit"
                    style={{
                      backgroundColor: '#CF2F2F',
                      borderRadius: '8px',
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 800,
                      fontSize: '11px',
                      letterSpacing: '0.05em',
                      color: '#fff',
                      height: '42px',
                      padding: '0 20px',
                      border: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                    className="transition-all hover:opacity-90 active:scale-95 uppercase"
                  >
                    <Search className="h-3.5 w-3.5" />
                    <span>FIND NOW</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

      </div>
    </section>
  )
}
